<template>
    <div class="digital-mailbox-email-list">
        <csp-digital-mailbox-email-list-table
            ref="emailListTable"
            :items-ajax-url="itemsAjaxUrl"
            table-headline="Digitale Mailbox"
            :updating-email-id="updatingEmailId"
            @row-clicked="onRowClicked"
            @row-contextmenu="onRowContextmenu">
            <template v-slot:above-body>
                <div class="py-2 d-flex align-items-center">
                    <b-button-group>
                        <b-button
                            :variant="mailType === 'received' ? 'primary' : 'secondary'"
                            @click="setMailType('received')">
                            Posteingang
                        </b-button>
                        <b-button
                            :variant="mailType === 'sent' ? 'primary' : 'secondary'"
                            @click="setMailType('sent')">
                            Postausgang
                        </b-button>
                    </b-button-group>
                    <csp-form-select-searchable
                        class="ml-3 digital-mailbox-mailbox-select"
                        :options="mailboxOptions"
                        :value="entraAppMailboxId"
                        @change="onMailboxChange"/>
                </div>
            </template>
            <template v-slot:cell(actions)="{ data }">
                <div class="table-actions">
                    <a
                        v-if="data.actions.conversationEmailCount > 1"
                        title="Konversation anzeigen"
                        @click.stop="openConversationModal(data.actions.conversationId)">
                        <i class="inticon-comment"/>
                    </a>
                    <a
                        v-if="data.actions.attachmentCount"
                        title="Anhänge anzeigen"
                        @click.stop="openAttachmentModal(data.actions.emailId)">
                        <i class="inticon-autoclaim_files"/>
                    </a>
                    <a
                        v-if="data.rowMeta.canAssignDamageRecordAppraisal"
                        title="Gutachten zuordnen"
                        @click.stop="openAssignModal(data.actions.emailId, data.actions.subject)">
                        <i class="inticon-autoclaim_assign"/>
                    </a>
                </div>
            </template>
        </csp-digital-mailbox-email-list-table>

        <csp-context-menu
            ref="contextMenu"
            :items="contextMenuItems"
            @select="onContextMenuSelect"/>

        <csp-email-category-edit-modal
            :email-id="selectedEmailId"
            :show-modal="showCategoryModal"
            @categories-changed="onCategoriesChanged"
            @update:showModal="onCategoryModalVisibilityChange"/>

        <csp-email-damage-record-assign-modal
            :email-id="selectedEmailId"
            :show-modal="showAssignModal"
            :subject="selectedSubject"
            @assignments-changed="onAssignmentsChanged"
            @update:showModal="onAssignModalVisibilityChange"/>

        <csp-email-set-reminder-modal
            :show="showReminderModal"
            @cancel="onReminderCancel"
            @confirm="onReminderConfirm"/>

        <csp-email-user-assignment-modal
            :email-id="contextMenuTargetRow ? contextMenuTargetRow.rowMeta.emailId : null"
            :initial-user-id="contextMenuTargetRow ? contextMenuTargetRow.rowMeta.assignedToUserId : null"
            :show="showUserAssignmentModal"
            @cancel="onUserAssignmentCancel"
            @confirm="onUserAssignmentConfirm"/>

        <csp-email-conversation-modal
            :conversation-id="selectedConversationId"
            :show="showConversationModal"
            @hidden="onConversationModalHidden"
            @open-email="onConversationModalOpenEmail"/>

        <csp-email-attachment-modal
            :email-id="attachmentModalEmailId"
            :show="showAttachmentModal"
            @hidden="onAttachmentModalHidden"/>

        <csp-email-detail-modal
            :form-context="detailModalFormContext"
            :show="showDetailModal"
            @email-updated="onDetailModalEmailUpdated"
            @hidden="onDetailModalHidden"/>
    </div>
</template>

<script>
    import '@desktop/scss/components/digital-mailbox.scss';

    import Container from '@services/Container';
    import FormContext from '@desktop/components/FormContext';
    import ValidationError from '@js/errors/ValidationError';
    import SelectSearchable from '@components/form/SelectSearchable';
    import EmailAttachmentModal from '@desktop/components/digital-mailbox/EmailAttachmentModal.vue';
    import EmailCategoryEditModal from '@desktop/components/digital-mailbox/EmailCategoryEditModal.vue';
    import EmailConversationModal from '@desktop/components/digital-mailbox/EmailConversationModal.vue';
    import EmailDamageRecordAssignModal from '@desktop/components/digital-mailbox/EmailDamageRecordAssignModal.vue';
    import EmailDetailModal from '@desktop/components/digital-mailbox/EmailDetailModal.vue';
    import EmailListTable from '@desktop/components/digital-mailbox/EmailListTable.vue';
    import EmailUserAssignmentModal from '@desktop/components/digital-mailbox/EmailUserAssignmentModal.vue';
    import EmailSetReminderModal from '@desktop/components/digital-mailbox/EmailSetReminderModal.vue';

    const container = Container.getInstance();
    const digitalMailboxRepository = container.getDigitalMailboxRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        components: {
            'csp-digital-mailbox-email-list-table': EmailListTable,
            'csp-email-attachment-modal': EmailAttachmentModal,
            'csp-email-category-edit-modal': EmailCategoryEditModal,
            'csp-email-conversation-modal': EmailConversationModal,
            'csp-email-damage-record-assign-modal': EmailDamageRecordAssignModal,
            'csp-email-detail-modal': EmailDetailModal,
            'csp-email-set-reminder-modal': EmailSetReminderModal,
            'csp-email-user-assignment-modal': EmailUserAssignmentModal,
            'csp-form-select-searchable': SelectSearchable,
        },
        data() {
            return {
                contextMenuRow: null, // the row-data of the currently open context menu
                contextMenuTargetRow: null, // snapshot of the row selected in contextmenu; used as base for modals
                attachmentModalEmailId: null,
                detailModalFormContext: new FormContext({
                    handleEditModeByQuery: false,
                    name: 'digital-mailbox-email-detail',
                }),
                detailModalHasChanges: false,
                entraAppMailboxId: null,
                mailboxes: [],
                mailType: 'received',
                reminderTargetEmailId: null,
                selectedConversationId: null,
                selectedEmailId: null,
                selectedSubject: '',
                showAssignModal: false,
                showAttachmentModal: false,
                showCategoryModal: false,
                showConversationModal: false,
                showDetailModal: false,
                showUserAssignmentModal: false,
                showReminderModal: false,
                updatingEmailId: null,
            };
        },
        computed: {
            contextMenuItems() {
                const row = this.contextMenuRow;
                if (!row || !row.rowMeta) {
                    return [];
                }
                const items = [];
                if (row.rowMeta.canAssignCategory) {
                    items.push({key: 'editCategory', label: 'Kategorien bearbeiten'});
                }
                if (row.rowMeta.canAssignDamageRecordAppraisal) {
                    items.push({key: 'assign', label: 'Gutachten zuordnen'});
                }
                if (row.rowMeta.canAssignUser) {
                    items.push({key: 'userAssignment', label: 'Bearbeiter zuweisen'});
                }
                if (row.rowMeta.canUpdateStatus) {
                    items.push(row.rowMeta.hasBeenRead
                        ? {key: 'markUnread', label: 'Als ungelesen markieren'}
                        : {key: 'markRead', label: 'Als gelesen markieren'});
                    items.push(row.rowMeta.status === 'done'
                        ? {key: 'markOpen', label: 'Als nicht erledigt markieren'}
                        : {key: 'markDone', label: 'Als erledigt markieren'});
                    items.push(row.rowMeta.hasReminderDate
                        ? {key: 'removeReminder', label: 'Wiedervorlagedatum entfernen'}
                        : {key: 'setReminder', label: 'Wiedervorlagedatum setzen'});
                }
                if (items.length === 0) {
                    return [{key: 'none', label: 'Keine Aktionen verfügbar', disabled: true}];
                }
                const isRowUpdating = this.updatingEmailId !== null
                    && String(this.updatingEmailId) === String(row.rowMeta.emailId);
                return items.map(item => isRowUpdating ? {...item, disabled: true} : item);
            },
            itemsAjaxUrl() {
                return `/api/desktop/digital-mailbox/emails?mailType=${this.mailType}`
                    + `&entraAppMailboxId=${this.entraAppMailboxId ?? ''}`;
            },
            mailboxOptions() {
                return [
                    {value: null, text: 'Alle'},
                    ...this.mailboxes,
                ];
            },
        },
        created() {
            digitalMailboxRepository.loadMailboxOptions().then((mailboxes) => {
                this.mailboxes = mailboxes;
            });
        },
        methods: {
            onAssignmentsChanged() {
                this.$refs.emailListTable.loadRecords();
            },
            onAssignModalVisibilityChange(value) {
                this.showAssignModal = value;
            },
            onAttachmentModalHidden() {
                this.showAttachmentModal = false;
            },
            onCategoriesChanged() {
                this.$refs.emailListTable.loadRecords();
            },
            onCategoryModalVisibilityChange(value) {
                this.showCategoryModal = value;
            },
            onDetailModalEmailUpdated() {
                this.detailModalHasChanges = true;
            },
            onDetailModalHidden() {
                this.showDetailModal = false;
                if (this.detailModalHasChanges) {
                    this.detailModalHasChanges = false;
                    this.$refs.emailListTable.loadRecords();
                }
            },
            onUserAssignmentCancel() {
                this.showUserAssignmentModal = false;
                this.contextMenuTargetRow = null;
            },
            onUserAssignmentConfirm({userId}) {
                this.showUserAssignmentModal = false;
                const row = this.contextMenuTargetRow;
                this.contextMenuTargetRow = null;
                if (!row) {
                    return;
                }
                const emailId = row.actions.emailId;
                this.updatingEmailId = emailId;
                digitalMailboxRepository.setEmailUserAssignment(emailId, userId)
                    .then((response) => {
                        this.$refs.emailListTable.updateRow(emailId, response.data.attributes);
                        flashMessenger.addSuccess('Bearbeiter wurde zugewiesen.');
                    })
                    .catch((error) => {
                        flashMessenger.addError(this._getErrorMessage(error));
                    })
                    .finally(() => {
                        this.updatingEmailId = null;
                    });
            },
            onContextMenuSelect(item) {
                const row = this.contextMenuRow;
                if (!row) {
                    return;
                }
                if (item.key === 'editCategory') {
                    this.openCategoryModal(row.actions.emailId, row.actions.subject);
                    return;
                }
                if (item.key === 'assign') {
                    this.openAssignModal(row.actions.emailId, row.actions.subject);
                    return;
                }
                if (item.key === 'userAssignment') {
                    this.contextMenuTargetRow = row;
                    this.showUserAssignmentModal = true;
                    return;
                }
                if (item.key === 'setReminder') {
                    // Snapshot the target row — contextMenuRow may be overwritten by the time the modal is confirmed.
                    this.contextMenuTargetRow = row;
                    this.showReminderModal = true;
                    return;
                }
                const emailId = row.rowMeta.emailId;
                this.updatingEmailId = emailId;
                digitalMailboxRepository.updateEmailStatus(emailId, item.key)
                    .then((response) => {
                        this.$refs.emailListTable.updateRow(emailId, response.data.attributes);
                        flashMessenger.addSuccess(this._getStatusUpdateSuccessMessage(item.key));
                    })
                    .catch((error) => {
                        flashMessenger.addError(this._getErrorMessage(error));
                    })
                    .finally(() => {
                        this.updatingEmailId = null;
                    });
            },
            onMailboxChange(option) {
                this.entraAppMailboxId = option ? option.value : null;

                this.$nextTick(() => {
                    this.$refs.emailListTable.loadRecords();
                });
            },
            onReminderCancel() {
                this.showReminderModal = false;
                this.contextMenuTargetRow = null;
            },
            onReminderConfirm(dateString) {
                this.showReminderModal = false;
                const row = this.contextMenuTargetRow;
                this.contextMenuTargetRow = null;
                if (!row) {
                    return;
                }
                const emailId = row.actions.emailId;
                this.updatingEmailId = emailId;
                digitalMailboxRepository.updateEmailStatus(
                    emailId,
                    'setReminder',
                    {reminderDate: dateString}
                )
                    .then((response) => {
                        this.$refs.emailListTable.updateRow(emailId, response.data.attributes);
                        flashMessenger.addSuccess(this._getStatusUpdateSuccessMessage('setReminder'));
                    })
                    .catch((error) => {
                        flashMessenger.addError(this._getErrorMessage(error));
                    })
                    .finally(() => {
                        this.updatingEmailId = null;
                    });
            },
            onRowClicked(item) {
                this.detailModalFormContext.objectId = item.emailId;
                this.detailModalFormContext.hasBeenRead = item.hasBeenRead;
                this.showDetailModal = true;
            },
            onConversationModalOpenEmail(item) {
                this.onRowClicked(item);
            },
            onConversationModalHidden() {
                this.showConversationModal = false;
            },
            onRowContextmenu(item, index, event) {
                event.preventDefault();
                this.contextMenuRow = item;
                this.$refs.contextMenu.openAt(event.clientX, event.clientY);
            },
            openAssignModal(emailId, subject) {
                this.selectedEmailId = emailId;
                this.selectedSubject = subject;
                this.showAssignModal = true;
            },
            openAttachmentModal(emailId) {
                this.attachmentModalEmailId = emailId;
                this.showAttachmentModal = true;
            },
            openCategoryModal(emailId) {
                this.selectedEmailId = emailId;
                this.showCategoryModal = true;
            },
            openConversationModal(conversationId) {
                this.selectedConversationId = conversationId;
                this.showConversationModal = true;
            },
            setMailType(mailType) {
                this.mailType = mailType;

                this.$nextTick(() => {
                    this.$refs.emailListTable.loadRecords();
                });
            },
            _getErrorMessage(error) {
                // basically, this is redundant to normal validation error handling, BUT we output
                // speficic validation error message instead of generic one that we use for forms
                // where the specific message is displayed at the for field.
                if (error instanceof ValidationError) {
                    const propertyErrors = error.getPropertyErrors();
                    if (propertyErrors.length > 0) {
                        return propertyErrors.map(propertyError => propertyError.title).join(' ');
                    }
                }
                return 'Die Aktion konnte nicht durchgeführt werden.';
            },
            _getStatusUpdateSuccessMessage(action) {
                const messages = {
                    markDone: 'E-Mail wurde als "erledigt" markiert.',
                    markOpen: 'E-Mail wurde als "offen" markiert.',
                    markRead: 'E-Mail wurde als "gelesen" markiert.',
                    markUnread: 'E-Mail wurde als "ungelesen" markiert.',
                    removeReminder: 'Wiedervorlagedatum wurde entfernt.',
                    setReminder: 'Wiedervorlagedatum wurde gesetzt.',
                };
                return messages[action] ?? 'Aktion wurde ausgeführt.';
            },
        },
    };
</script>
